/**
 * IRONMASTER PACKAGE DISCOUNT — STOREFRONT ENGINE (vanilla, no build step)
 * =======================================================================
 * Port of the Shopify Function's discount-config.js + discount-engine.js (v5)
 * to plain browser JS so the Snow theme cart drawer / full cart can render the
 * live "Everyday Package Discount Program" progress bar WITHOUT a React/bundler
 * pipeline.
 *
 * SINGLE SOURCE OF TRUTH: the values below MUST mirror the Shopify Function's
 * src/discount-config.js. The Function applies the real discount at checkout;
 * this file only mirrors the same logic to show accurate progress in the UI.
 *
 * Mounts into any element with [data-im-discount]. Re-renders on Snow's
 * `cart:change` / `cart:refresh` events and on load.
 */
(function () {
  "use strict";

  // ─── CONFIG (mirror of discount-config.js v5) ───────────────────────────────

  var ATTACHMENT_SKUS = {
    BENCH: [
      "1001","1003","1004-2","1005-2","1006","1007-2","1010","1025","1028",
      "1028-2","1031","1032","1033","1041-2","1044","1070-2","1086","1211",
      "3108","3109","3112","3114","3119","3224"
    ],
    QLDB_75: [
      "1008","1009-2","1012-2","1014-2","1017","1021","1032","1043","1050",
      "1051","1068","1069","120","1201","1202","1204","1205","165","3113",
      "3119","3120","3122","3221","3222","75-2"
    ],
    IM2000_3000: [
      "1001","1003","1004-2","1005-2","1006","1007-2","1008","1009","1010",
      "1014-2","1017","1021","1024","1025","1026","1027","1028","1028-2",
      "1030","1031","1032","1033","1041-2","1043","1047-2","1050","1051",
      "1064","1068","1069","1070-2","1074","1077-2","1078","120","1201",
      "1202","1204","1205","1211","1212","165","3000","3100","3104","3108",
      "3109","3112","3113","3114","3116","3117","3119","3120","3122",
      "320olyset","3221","3222","3223","3224","3337","3339","45","75-2",
      "CCB","LRB","OB86","OCB","oly275","oly415","oly460","SP50P","SPH","TRICEPROPE"
    ]
  };

  var EXCLUDED_TAGS = ["im-excluded-lifestyle", "im-excluded-giftcard"];
  var B2B_CUSTOMER_TAG = "im-customer-b2b";

  // SKU → anchor tag registry (mirror of discount-config SKU_TAGS). Used to
  // synthesize anchor tags for the Package Builder's live discount preview when
  // real product tags aren't in the DOM. QLDB anchor SKUs are provisional per
  // Q8.1 (README v4) — both the placeholder and "45"/"75-2" forms are mapped.
  var SKU_TAGS = {
    // Verified against live ironmaster-5648 catalog: SKU 1001 = Super Bench PRO V2,
    // SKU 1003 = Super Bench Adjustable (reverse of the v5 config comments).
    "1001": "im-anchor-bench-superpro",
    "1003": "im-anchor-bench-super",
    "1500": "im-anchor-rack-1500",
    "2000": "im-anchor-rack-2000",
    "2001": "im-anchor-rack-2000",
    "3000": "im-anchor-rack-3000",
    "45":     "im-anchor-qldb-45",
    "qldb45": "im-anchor-qldb-45",
    "75-2":   "im-anchor-qldb-75",
    "qldb75": "im-anchor-qldb-75"
  };
  function tagsForSku(sku) {
    var key = String(sku || "").toLowerCase().trim();
    return SKU_TAGS[key] ? [SKU_TAGS[key]] : [];
  }

  var PACKAGE_RULES = [
    { id: "package-7-im3000", label: "IM3000 Custom Home Gym — Everyday Package Discount",
      anchorTags: ["im-anchor-rack-3000"], attachmentTag: "collection:im3000-valhalla-custom-package", minimumDistinctAttachments: 0,
      priceTiers: [ {minTotal:5001,maxTotal:5600,discountPercent:8}, {minTotal:5601,maxTotal:6500,discountPercent:10}, {minTotal:6501,maxTotal:Infinity,discountPercent:12} ],
      progressMessage: function (r){ return "Add $" + r.toFixed(0) + " more to your IM3000 package to unlock a bigger discount"; },
      activeMessage: function (p){ return "IM3000 Custom Home Gym — " + p + "% Everyday Package Discount applied"; } },
    { id: "package-4-im2000", label: "IM2000 Custom Home Gym — Everyday Package Discount",
      anchorTags: ["im-anchor-rack-2000"], attachmentTag: "collection:im2000-custom-package", minimumDistinctAttachments: 0,
      priceTiers: [ {minTotal:2401,maxTotal:2900,discountPercent:8}, {minTotal:2901,maxTotal:3900,discountPercent:10}, {minTotal:3901,maxTotal:Infinity,discountPercent:12} ],
      progressMessage: function (r){ return "Add $" + r.toFixed(0) + " more to your IM2000 package to unlock a bigger discount"; },
      activeMessage: function (p){ return "IM2000 Custom Home Gym — " + p + "% Everyday Package Discount applied"; } },
    { id: "package-3-im1500", label: "IM1500 Custom Home Gym — Everyday Package Discount",
      anchorTags: ["im-anchor-rack-1500"], attachmentSkus: ATTACHMENT_SKUS.IM2000_3000, minimumDistinctAttachments: 0,
      priceTiers: [ {minTotal:1300,maxTotal:2000,discountPercent:8}, {minTotal:2001,maxTotal:3000,discountPercent:10}, {minTotal:3001,maxTotal:Infinity,discountPercent:12} ],
      progressMessage: function (r){ return "Add $" + r.toFixed(0) + " more to your IM1500 package to unlock a bigger discount"; },
      activeMessage: function (p){ return "IM1500 Custom Home Gym — " + p + "% Everyday Package Discount applied"; } },
    { id: "package-2d-qldb75-superpro", label: "75lb QLDB + Super Bench Pro — Everyday Package Discount",
      anchorTags: ["im-anchor-qldb-75","im-anchor-bench-superpro"], attachmentTag: "collection:bench-and-dumbbell-custom-gym-package", minimumDistinctAttachments: 0,
      priceTiers: [ {minTotal:1200,maxTotal:1500,discountPercent:8}, {minTotal:1501,maxTotal:2000,discountPercent:10}, {minTotal:2001,maxTotal:Infinity,discountPercent:12} ],
      progressMessage: function (r){ return "Add $" + r.toFixed(0) + " more to unlock a bigger Everyday Package Discount"; },
      activeMessage: function (p){ return "75lb QLDB + Super Bench Pro — " + p + "% Everyday Package Discount applied"; } },
    { id: "package-2c-qldb75-super", label: "75lb QLDB + Super Bench — Everyday Package Discount",
      anchorTags: ["im-anchor-qldb-75","im-anchor-bench-super"], attachmentTag: "collection:bench-and-dumbbell-custom-gym-package", minimumDistinctAttachments: 0,
      priceTiers: [ {minTotal:1200,maxTotal:1500,discountPercent:8}, {minTotal:1501,maxTotal:2000,discountPercent:10}, {minTotal:2001,maxTotal:Infinity,discountPercent:12} ],
      progressMessage: function (r){ return "Add $" + r.toFixed(0) + " more to unlock a bigger Everyday Package Discount"; },
      activeMessage: function (p){ return "75lb QLDB + Super Bench — " + p + "% Everyday Package Discount applied"; } },
    { id: "package-2b-qldb45-superpro", label: "45lb QLDB + Super Bench Pro — Everyday Package Discount",
      anchorTags: ["im-anchor-qldb-45","im-anchor-bench-superpro"], attachmentTag: "collection:bench-and-dumbbell-custom-gym-package", minimumDistinctAttachments: 0,
      priceTiers: [ {minTotal:1200,maxTotal:1500,discountPercent:8}, {minTotal:1501,maxTotal:2000,discountPercent:10}, {minTotal:2001,maxTotal:Infinity,discountPercent:12} ],
      progressMessage: function (r){ return "Add $" + r.toFixed(0) + " more to unlock a bigger Everyday Package Discount"; },
      activeMessage: function (p){ return "45lb QLDB + Super Bench Pro — " + p + "% Everyday Package Discount applied"; } },
    { id: "package-2a-qldb45-super", label: "45lb QLDB + Super Bench — Everyday Package Discount",
      anchorTags: ["im-anchor-qldb-45","im-anchor-bench-super"], attachmentTag: "collection:bench-and-dumbbell-custom-gym-package", minimumDistinctAttachments: 0,
      priceTiers: [ {minTotal:1200,maxTotal:1500,discountPercent:8}, {minTotal:1501,maxTotal:2000,discountPercent:10}, {minTotal:2001,maxTotal:Infinity,discountPercent:12} ],
      progressMessage: function (r){ return "Add $" + r.toFixed(0) + " more to unlock a bigger Everyday Package Discount"; },
      activeMessage: function (p){ return "45lb QLDB + Super Bench — " + p + "% Everyday Package Discount applied"; } },
    { id: "package-6-qldb75-standalone", label: "75lb Quick-Lock Dumbbells — Everyday Package Discount",
      anchorTags: ["im-anchor-qldb-75"], attachmentTag: "collection:quick-lock-adjustable-dumbbells-package", minimumDistinctAttachments: 0,
      priceTiers: [ {minTotal:1100,maxTotal:1300,discountPercent:5}, {minTotal:1301,maxTotal:1600,discountPercent:8}, {minTotal:1601,maxTotal:Infinity,discountPercent:10} ],
      progressMessage: function (r){ return "Add $" + r.toFixed(0) + " more to your 75lb QLDB package to unlock a bigger discount"; },
      activeMessage: function (p){ return "75lb Quick-Lock Dumbbells — " + p + "% Everyday Package Discount applied"; } },
    { id: "package-6-qldb45-standalone", label: "45lb Quick-Lock Dumbbells — Everyday Package Discount",
      anchorTags: ["im-anchor-qldb-45"], attachmentTag: "collection:quick-lock-adjustable-dumbbells-package", minimumDistinctAttachments: 0,
      priceTiers: [ {minTotal:1100,maxTotal:1300,discountPercent:5}, {minTotal:1301,maxTotal:1600,discountPercent:8}, {minTotal:1601,maxTotal:Infinity,discountPercent:10} ],
      progressMessage: function (r){ return "Add $" + r.toFixed(0) + " more to your 45lb QLDB package to unlock a bigger discount"; },
      activeMessage: function (p){ return "45lb Quick-Lock Dumbbells — " + p + "% Everyday Package Discount applied"; } },
    { id: "package-1b-superpro", label: "Super Bench Pro Package — Everyday Package Discount",
      anchorTags: ["im-anchor-bench-superpro"], attachmentTag: "collection:super-bench-with-two-or-more-attachments", minimumDistinctAttachments: 2,
      priceTiers: [ {minTotal:520,maxTotal:700,discountPercent:8}, {minTotal:701,maxTotal:1000,discountPercent:10}, {minTotal:1001,maxTotal:Infinity,discountPercent:12} ],
      progressMessage: function (r){ return r > 0 ? "Add " + r + " more attachment" + (r === 1 ? "" : "s") : "Add more items to unlock a bigger discount"; },
      activeMessage: function (p){ return "Super Bench Pro Package — " + p + "% Everyday Package Discount applied"; } },
    { id: "package-1a-super", label: "Super Bench Package — Everyday Package Discount",
      anchorTags: ["im-anchor-bench-super"], attachmentTag: "collection:super-bench-with-two-or-more-attachments", minimumDistinctAttachments: 2,
      priceTiers: [ {minTotal:520,maxTotal:700,discountPercent:8}, {minTotal:701,maxTotal:1000,discountPercent:10}, {minTotal:1001,maxTotal:Infinity,discountPercent:12} ],
      progressMessage: function (r){ return r > 0 ? "Add " + r + " more attachment" + (r === 1 ? "" : "s") : "Add more items to unlock a bigger discount"; },
      activeMessage: function (p){ return "Super Bench Package — " + p + "% Everyday Package Discount applied"; } }
  ];

  var UI_CONFIG = {
    programName: "Everyday Package Discount Program",
    currencySymbol: "$",
    showNextTierProgress: true,
    // Matches the Figma reference: a monochrome ink/gray tier bar (no per-tier
    // rainbow coding) with green reserved for "reached / discount applied" states.
    colors: {
      progressBarActive: "#1c1c1c",
      progressBarInactive: "#e0dfdd",
      savingsText: "#039935",
      messageText: "#566573",
      tier1: "#1c1c1c",
      tier2: "#1c1c1c",
      tier3: "#1c1c1c"
    }
  };

  // ─── ENGINE (mirror of discount-engine.js v5) ───────────────────────────────

  function lineHasTag(line, tag) {
    var tags = (line.merchandise && line.merchandise.product && line.merchandise.product.tags) || [];
    var t = tag.toLowerCase().trim();
    return tags.some(function (x) { return String(x).toLowerCase().trim() === t; });
  }
  function lineIsExcluded(line) { return EXCLUDED_TAGS.some(function (tag) { return lineHasTag(line, tag); }); }
  function customerIsB2B(customerTags) {
    return (customerTags || []).map(function (t) { return String(t).toLowerCase().trim(); }).indexOf(B2B_CUSTOMER_TAG.toLowerCase()) !== -1;
  }
  function lineSkuInList(line, skuList) {
    var sku = ((line.merchandise && line.merchandise.sku) || "").toLowerCase().trim();
    return skuList.some(function (s) { return String(s).toLowerCase().trim() === sku; });
  }
  function anchorsPresent(rule, lines) {
    return rule.anchorTags.every(function (tag) { return lines.some(function (line) { return lineHasTag(line, tag); }); });
  }
  function lineIsQualifyingAttachment(rule, line) {
    return rule.attachmentTag ? lineHasTag(line, rule.attachmentTag) : lineSkuInList(line, rule.attachmentSkus);
  }
  function countDistinctAttachmentLines(rule, lines) {
    var attachmentQty = lines.filter(function (line) {
      return lineIsQualifyingAttachment(rule, line) && !rule.anchorTags.some(function (tag) { return lineHasTag(line, tag); });
    }).reduce(function (sum, line) { return sum + line.quantity; }, 0);
    var anchorExtraQty = lines.filter(function (line) {
      return rule.anchorTags.some(function (tag) { return lineHasTag(line, tag); }) && lineIsQualifyingAttachment(rule, line);
    }).reduce(function (sum, line) { return sum + Math.max(0, line.quantity - 1); }, 0);
    return attachmentQty + anchorExtraQty;
  }
  function getQualifyingLines(rule, lines) {
    var seen = {}, result = [];
    lines.forEach(function (line) {
      if (rule.anchorTags.some(function (tag) { return lineHasTag(line, tag); }) && !seen[line.id]) { seen[line.id] = 1; result.push(line); }
    });
    lines.forEach(function (line) {
      if (!seen[line.id] && lineIsQualifyingAttachment(rule, line) && !rule.anchorTags.some(function (tag) { return lineHasTag(line, tag); })) { seen[line.id] = 1; result.push(line); }
    });
    return result;
  }
  function getQualifyingTotal(rule, lines) {
    return getQualifyingLines(rule, lines).reduce(function (sum, line) {
      var price = parseFloat((line.merchandise && line.merchandise.price && line.merchandise.price.amount) || 0);
      return sum + price * line.quantity;
    }, 0);
  }
  function getTierForTotal(rule, qualifyingTotal) {
    var matching = rule.priceTiers.filter(function (tier) { return qualifyingTotal >= tier.minTotal && qualifyingTotal <= tier.maxTotal; });
    if (matching.length === 0) return null;
    return matching.reduce(function (best, curr) { return curr.discountPercent > best.discountPercent ? curr : best; });
  }
  function calculateSavings(lines, discountPercent) {
    var subtotal = lines.reduce(function (sum, line) {
      var price = parseFloat((line.merchandise && line.merchandise.price && line.merchandise.price.amount) || 0);
      return sum + price * line.quantity;
    }, 0);
    return parseFloat(((subtotal * discountPercent) / 100).toFixed(2));
  }
  function rulesToConsider(ruleIds) {
    if (!ruleIds) return PACKAGE_RULES;
    return PACKAGE_RULES.filter(function (rule) { return ruleIds.indexOf(rule.id) !== -1; });
  }
  function findBestMatch(allLines, customerTags, ruleIds) {
    if (customerIsB2B(customerTags)) return null;
    var lines = allLines.filter(function (line) { return !lineIsExcluded(line); });
    if (lines.length === 0) return null;
    var bestMatch = null, bestSavings = 0;
    rulesToConsider(ruleIds).forEach(function (rule) {
      if (!anchorsPresent(rule, lines)) return;
      if (countDistinctAttachmentLines(rule, lines) < rule.minimumDistinctAttachments) return;
      var qualifyingTotal = getQualifyingTotal(rule, lines);
      var tierResult = getTierForTotal(rule, qualifyingTotal);
      if (!tierResult) return;
      var qualifyingLines = getQualifyingLines(rule, lines);
      var savingsAmount = calculateSavings(qualifyingLines, tierResult.discountPercent);
      if (savingsAmount > bestSavings) {
        bestSavings = savingsAmount;
        bestMatch = {
          ruleId: rule.id, label: rule.label, discountPercent: tierResult.discountPercent,
          qualifyingTotal: qualifyingTotal, qualifyingLines: qualifyingLines, savingsAmount: savingsAmount,
          message: rule.activeMessage(tierResult.discountPercent)
        };
      }
    });
    return bestMatch;
  }
  function calculateSegmentFills(priceTiers, qualifyingTotal) {
    return priceTiers.map(function (tier, i) {
      var segStart = i === 0 ? 0 : priceTiers[i - 1].minTotal;
      var segEnd = tier.minTotal;
      if (qualifyingTotal <= segStart) return 0;
      if (qualifyingTotal >= segEnd) return 100;
      return Math.round(((qualifyingTotal - segStart) / (segEnd - segStart)) * 100);
    });
  }
  function calculateProgress(allLines, customerTags, ruleIds) {
    if (customerIsB2B(customerTags)) return [];
    var lines = allLines.filter(function (line) { return !lineIsExcluded(line); });
    return rulesToConsider(ruleIds).map(function (rule) {
      var hasAnchors = anchorsPresent(rule, lines);
      var attachmentCount = countDistinctAttachmentLines(rule, lines);
      var qualifyingTotal = hasAnchors ? getQualifyingTotal(rule, lines) : 0;
      var tierResult = hasAnchors ? getTierForTotal(rule, qualifyingTotal) : null;
      var isActive = hasAnchors && attachmentCount >= rule.minimumDistinctAttachments && tierResult !== null;
      var nextTier = rule.priceTiers.filter(function (t) { return qualifyingTotal < t.minTotal; })[0];
      var missingAttachments = Math.max(0, rule.minimumDistinctAttachments - attachmentCount);
      var remainingForNextTier = nextTier ? nextTier.minTotal - qualifyingTotal : 0;
      var lowestTierMin = (rule.priceTiers[0] && rule.priceTiers[0].minTotal) || 0;
      var progressPercent = isActive ? 100 : (qualifyingTotal > 0 && lowestTierMin > 0 ? Math.min(99, Math.round((qualifyingTotal / lowestTierMin) * 100)) : 0);
      return {
        ruleId: rule.id, label: rule.label, isActive: isActive, progressPercent: progressPercent,
        priceTiers: rule.priceTiers, qualifyingTotal: qualifyingTotal,
        currentDiscount: tierResult ? tierResult.discountPercent : null,
        nextTierDiscount: nextTier ? nextTier.discountPercent : null,
        remainingForNextTier: remainingForNextTier > 0 ? remainingForNextTier : 0,
        missingAttachments: missingAttachments,
        message: isActive ? rule.activeMessage(tierResult.discountPercent)
                          : rule.progressMessage(missingAttachments > 0 ? missingAttachments : remainingForNextTier)
      };
    });
  }
  function getNextTierNudge(allLines, customerTags, ruleIds) {
    var bestMatch = findBestMatch(allLines, customerTags, ruleIds);
    var progress = calculateProgress(allLines, customerTags, ruleIds);
    if (progress.length === 0) return null;
    if (bestMatch) {
      var active = progress.filter(function (p) { return p.ruleId === bestMatch.ruleId; })[0];
      if (active && active.remainingForNextTier > 0) return active;
      return null;
    }
    var candidates = progress.filter(function (p) { return !p.isActive && p.progressPercent > 0; });
    if (candidates.length === 0) return null;
    return candidates.reduce(function (best, curr) { return curr.progressPercent > best.progressPercent ? curr : best; });
  }

  // ─── SHOPIFY CART ADAPTER ───────────────────────────────────────────────────

  var productTagsCache = {};
  var compareAtPriceCache = {};

  function cacheCompareAtPrices(product) {
    if (!product || !product.variants) return;
    product.variants.forEach(function (variant) {
      var compareAt = variant.compare_at_price;
      compareAtPriceCache[String(variant.id)] =
        compareAt && compareAt > variant.price ? compareAt : 0;
    });
  }

  function compareAtPriceForVariant(variantId) {
    return compareAtPriceCache[String(variantId)] || 0;
  }
  function fetchProductTagsForHandles(handles) {
    var uncached = handles.filter(function (h) { return h && !productTagsCache[h]; });
    return Promise.all(uncached.map(function (h) {
      return fetch("/products/" + h + ".js")
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          productTagsCache[h] = (p && p.tags) || [];
          cacheCompareAtPrices(p);
        })
        .catch(function () { productTagsCache[h] = []; });
    })).then(function () { return productTagsCache; });
  }

  var PACKAGE_DISCOUNT_TITLE_RE = /everyday package discount/i;

  function fetchCartLines() {
    return fetch("/cart.js", { headers: { "Content-Type": "application/json" } })
      .then(function (r) { if (!r.ok) throw new Error("cart fetch failed"); return r.json(); })
      .then(function (cart) {
        var handles = (cart.items || []).map(function (item) { return item.handle; });
        return fetchProductTagsForHandles(handles).then(function () { return cart; });
      })
      .then(function (cart) {
        var lines = (cart.items || []).map(function (item) {
          var tags = productTagsCache[item.handle] || [];
          if (!tags.length) tags = tagsForSku(item.sku);
          return {
            id: item.key,
            quantity: item.quantity,
            merchandise: {
              sku: item.sku || "",
              price: { amount: (item.original_price / 100).toFixed(2) },
              product: {
                id: String(item.product_id),
                title: item.product_title,
                tags: tags
              }
            }
          };
        });
        // cart.total_price is the REAL, already-discounted total (the
        // Function is live) — this is what the "$X - in your cart" mobile
        // badge should show, not the package-qualifying subtotal.
        var packageApplied = (cart.items || []).some(function (item) {
          return (item.line_level_discount_allocations || []).some(function (allocation) {
            return PACKAGE_DISCOUNT_TITLE_RE.test((allocation.discount_application && allocation.discount_application.title) || "");
          });
        });
        var hasDiscountCode = (cart.discount_codes || []).some(function (code) { return code.applicable; });
        return {
          lines: lines,
          cartTotal: cart.total_price / 100,
          attributes: cart.attributes || {},
          discounts: { packageApplied: packageApplied, hasDiscountCode: hasDiscountCode }
        };
      });
  }

  // ─── RENDER ─────────────────────────────────────────────────────────────────

  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); }

  function getCartTitles() {
    var el = document.querySelector("[data-pkg-cart-titles]");
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  }

  var PKG_LABEL_SUFFIX_RE = /\s*[—-]\s*Everyday Package Discount$/i;

  function packageLabel(match) { return match.label.replace(PKG_LABEL_SUFFIX_RE, ""); }
  function packageCartTitle(match) { return getCartTitles()[match.ruleId] || packageLabel(match); }

  var ICON_CHECK = '<svg width="16" height="16" viewBox="0 0 18 18" fill="none" aria-hidden="true"><circle cx="9" cy="9" r="9" fill="#039935"/><path d="M5 9.2l2.5 2.5L13 6.3" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_SEAL = '<svg width="18" height="18" viewBox="0 0 13 13" fill="none" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M4.23526 1.0325C4.7847 0.400621 5.59555 0 6.5 0C7.40441 0 8.21522 0.400581 8.76466 1.03241C9.60007 0.974042 10.4568 1.26411 11.0964 1.90372C11.736 2.54332 12.0261 3.40006 11.9677 4.23546C12.5995 4.7849 13 5.59566 13 6.5C13 7.40451 12.5993 8.2154 11.9674 8.76485C12.0256 9.60012 11.7355 10.4567 11.096 11.0962C10.4565 11.7357 9.59999 12.0257 8.76472 11.9675C8.21528 12.5994 7.40445 13 6.5 13C5.5956 13 4.7848 12.5994 4.23536 11.9676C3.39994 12.026 2.5432 11.7359 1.90358 11.0963C1.26397 10.4567 0.973906 9.59995 1.03227 8.76454C0.400524 8.2151 0 7.40435 0 6.5C0 5.59561 0.400564 4.78482 1.03237 4.23537C0.974067 3.40003 1.26414 2.54338 1.90369 1.90383C2.54325 1.26427 3.39991 0.974196 4.23526 1.0325ZM8.90687 5.29062C9.06737 5.06591 9.01533 4.75364 8.79062 4.59313C8.56591 4.43263 8.25364 4.48467 8.09313 4.70938L5.9362 7.72909L4.85355 6.64645C4.65829 6.45118 4.34171 6.45118 4.14645 6.64645C3.95118 6.84171 3.95118 7.15829 4.14645 7.35355L5.64645 8.85355C5.75037 8.95748 5.89476 9.01042 6.04124 8.9983C6.18772 8.98617 6.32144 8.91022 6.40687 8.79062L8.90687 5.29062Z" fill="#16A34A"/></svg>';
  var ICON_TAG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20.59 13.41L11.42 22.58a2 2 0 01-2.83 0L1.41 15.41a2 2 0 010-2.83l9.17-9.17A2 2 0 0112 3h7a2 2 0 012 2v7a2 2 0 01-.58 1.41z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><circle cx="14.5" cy="8.5" r="1.2" fill="currentColor"/></svg>';

  function tierRangeLabel(tier) {
    var sym = UI_CONFIG.currencySymbol;
    if (tier.maxTotal === Infinity) return sym + tier.minTotal.toLocaleString() + '+ · ' + tier.discountPercent + '%';
    return sym + tier.minTotal.toLocaleString() + ' – ' + tier.maxTotal.toLocaleString() + ' · ' + tier.discountPercent + '%';
  }
  // Same label, but with the $ range wrapped in its own span so a narrow-screen
  // context (see .pkg-progress mobile rules in ironmaster.css) can hide it and
  // show just the percentage — matches the simplified mobile tier bar reference.
  function tierRangeLabelHTML(tier) {
    var sym = UI_CONFIG.currencySymbol;
    var range = tier.maxTotal === Infinity
      ? sym + tier.minTotal.toLocaleString() + '+'
      : sym + tier.minTotal.toLocaleString() + ' – ' + tier.maxTotal.toLocaleString();
    return '<span class="im-discount__range">' + range + ' · </span>' + tier.discountPercent + '%';
  }
  function formatRemaining(n) { return UI_CONFIG.currencySymbol + Math.max(0, n).toFixed(0); }
  function formatMoneyFull(n) {
    return UI_CONFIG.currencySymbol + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function renderHTML(lines, cartTotal, ruleIds, cartDiscounts) {
    if (!lines || lines.length === 0) return "";
    var bestMatch = findBestMatch(lines, null, ruleIds);
    var progress = calculateProgress(lines, null, ruleIds);
    var nextNudge = getNextTierNudge(lines, null, ruleIds);
    var activeProgress = bestMatch
      ? progress.filter(function (p) { return p.ruleId === bestMatch.ruleId; })[0]
      : (nextNudge ? progress.filter(function (p) { return p.ruleId === nextNudge.ruleId; })[0] : null);
    if (!bestMatch && !activeProgress) return "";

    var c = UI_CONFIG.colors;
    var html = '<div class="im-discount"><div class="im-discount__row">';

    if (bestMatch) {
      // "Super Bench Package — Everyday Package Discount" -> "Super Bench Package"
      var shortLabel = packageCartTitle(bestMatch);
      var packageMissing = !!(cartDiscounts && !cartDiscounts.packageApplied);
      // Cart-value badge is desktop-hidden by default; the mobile reference
      // (package-builder page only) shows it beside the banner — see the
      // .pkg-progress mobile rules in ironmaster.css.
      if (!packageMissing) {
        html +=
          '<div class="im-discount__banner">' +
            ICON_SEAL +
            '<div class="im-discount__banner-text">' +
              '<p class="im-discount__badge">' + esc(shortLabel) + ' — ' + bestMatch.discountPercent + '%</p>' +
              '<p class="im-discount__savings">Discount applied</p>' +
            '</div>' +
          '</div>';
      } else if (cartDiscounts.hasDiscountCode) {
        html +=
          '<div class="im-discount__banner im-discount__banner--off">' +
            '<div class="im-discount__banner-text">' +
              '<p class="im-discount__badge">' + esc(shortLabel) + ' — ' + bestMatch.discountPercent + '%</p>' +
              '<p class="im-discount__savings">Not combinable with your discount code</p>' +
            '</div>' +
          '</div>';
      }
      html +=
        '<button type="button" class="im-discount__cart-value" data-im-cart-value aria-controls="cart-drawer" aria-expanded="false">' +
          '<svg width="14" height="13" viewBox="0 0 18 16" fill="none" aria-hidden="true"><path d="M1 1h2l2.2 9.5a1.5 1.5 0 001.47 1.2h7.6a1.5 1.5 0 001.47-1.18L17.5 5H4.6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><circle cx="7.5" cy="14" r="1.1" fill="currentColor"/><circle cx="14" cy="14" r="1.1" fill="currentColor"/></svg>' +
          '<span>' + formatMoneyFull(cartTotal || 0) + ' - in your cart</span>' +
        '</button>';
    }

    if (activeProgress) {
      var tiers = activeProgress.priceTiers;
      // QA fix (IRON-9, Elias 28-Jul): qualifyingTotal sums $ across every
      // qualifying line regardless of distinct product count, so stacking
      // quantity on a single attachment alone could fill/"reach" tiers here —
      // no amount of $ on one product satisfies the distinct-attachment gate,
      // so don't show dollar-tier progress at all until that gate is cleared.
      var total = activeProgress.missingAttachments > 0 ? 0 : activeProgress.qualifyingTotal;
      var fills = calculateSegmentFills(tiers, total);
      // The "target" tier is the first one not yet reached — it gets a left-aligned
      // range label AND a right-aligned "add $X more" nudge. Tiers before it are
      // "reached" (checkmark, left-aligned). Any tier after it is future/muted; the
      // very last tier centers itself when it isn't the active target (matches the
      // Figma reference: reached=left+check, current target=left+right split,
      // future last tier=centered).
      var targetIndex = -1;
      for (var i = 0; i < tiers.length; i++) { if (total < tiers[i].minTotal) { targetIndex = i; break; } }

      var barLabel = tiers.map(function (t) {
        return tierRangeLabel(t) + (total >= t.minTotal ? ' (reached)' : '');
      }).join('; ');

      html += '<div class="im-discount__seg" role="group" aria-label="Package discount tiers: ' + esc(barLabel) + '">';
      tiers.forEach(function (tier, i) {
        var reached = total >= tier.minTotal;
        var isTarget = i === targetIndex;
        var isLast = i === tiers.length - 1;
        var align = isTarget ? 'split' : (isLast ? 'center' : 'left');
        html += '<div class="im-discount__col im-discount__col--' + align + '">' +
                  '<div class="im-discount__col-top">' +
                    '<span class="im-discount__col-label' + (reached ? ' is-reached' : '') + '">' +
                      (reached ? ICON_CHECK : '') + '<span>' + tierRangeLabelHTML(tier) + '</span>' +
                    '</span>' +
                    (isTarget && UI_CONFIG.showNextTierProgress ?
                      // QA fix (IRON-9, Elias 28-Jul): a $-only nudge here is
                      // misleading while the distinct-attachment requirement
                      // still isn't met — it reads as "spend $X more" when the
                      // real blocker is "add another distinct product," which
                      // no amount of $ on the current attachment satisfies.
                      (activeProgress.missingAttachments > 0
                        ? '<span class="im-discount__col-nudge">' + ICON_TAG + '<span>' + esc(activeProgress.message) + ' <b>to reach ' + tier.discountPercent + '%</b></span></span>'
                        : '<span class="im-discount__col-nudge">' + ICON_TAG + '<span>Add ' + formatRemaining(tier.minTotal - total) + ' more <b>to reach ' + tier.discountPercent + '%</b></span></span>')
                      : '') +
                  '</div>' +
                  '<div class="im-discount__slot" style="background:' + c.progressBarInactive + '">' +
                    '<div class="im-discount__fill" style="width:' + fills[i] + '%;background:' + c.progressBarActive + '"></div>' +
                  '</div>' +
                '</div>';
      });
      html += '</div>';
    }
    html += '</div></div>';
    return html;
  }

  var lastCartLines = [];
  var lastCartTotal = 0;
  var lastCartAttributes = null;
  var lastCartDiscounts = null;

  var PKG_ATTR_TITLE = "_im_pkg_title";
  var PKG_ATTR_RULE = "_im_pkg_rule";
  var PKG_ATTR_PERCENT = "_im_pkg_percent";
  var SYNC_RETRY_DELAYS = [400, 1500, 4000];
  var CHECKOUT_GATE_TIMEOUT = 1500;

  var syncPending = null;
  var syncAttempt = 0;
  var syncTimer = null;
  var checkoutGateBypass = false;

  function desiredPackageAttributes() {
    var bestMatch = findBestMatch(lastCartLines);
    return {
      title: bestMatch ? packageCartTitle(bestMatch) : "",
      rule: bestMatch ? bestMatch.ruleId : "",
      percent: bestMatch ? String(bestMatch.discountPercent) : ""
    };
  }

  function packageAttributesInSync(wanted) {
    if (lastCartAttributes === null) return false;
    return (lastCartAttributes[PKG_ATTR_TITLE] || "") === wanted.title &&
           (lastCartAttributes[PKG_ATTR_RULE] || "") === wanted.rule &&
           (lastCartAttributes[PKG_ATTR_PERCENT] || "") === wanted.percent;
  }

  function packageAttributesNeedSync() {
    if (lastCartAttributes === null) return true;
    return !packageAttributesInSync(desiredPackageAttributes());
  }

  function writePackageCartAttributes(wanted) {
    var attributes = {};
    attributes[PKG_ATTR_TITLE] = wanted.title;
    attributes[PKG_ATTR_RULE] = wanted.rule;
    attributes[PKG_ATTR_PERCENT] = wanted.percent;
    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (r) {
      if (!r.ok) throw new Error("cart attribute sync failed: " + r.status);
      return r.json();
    }).then(function (cart) {
      lastCartAttributes = (cart && cart.attributes) || lastCartAttributes;
      syncAttempt = 0;
    });
  }

  function scheduleSyncRetry() {
    if (syncAttempt >= SYNC_RETRY_DELAYS.length) { syncAttempt = 0; return; }
    var delay = SYNC_RETRY_DELAYS[syncAttempt];
    syncAttempt += 1;
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(function () { syncTimer = null; syncPackageCartAttributes(); }, delay);
  }

  function syncPackageCartAttributes() {
    if (lastCartAttributes === null) return null;
    if (syncPending) return syncPending;
    var wanted = desiredPackageAttributes();
    if (packageAttributesInSync(wanted)) return null;
    syncPending = writePackageCartAttributes(wanted)
      .catch(function () { scheduleSyncRetry(); })
      .then(function () { syncPending = null; });
    return syncPending;
  }

  function whenPackageAttributesSynced() {
    var timeout = new Promise(function (resolve) { setTimeout(resolve, CHECKOUT_GATE_TIMEOUT); });
    var attempt = lastCartAttributes === null
      ? refresh().then(function () { return syncPackageCartAttributes(); })
      : syncPackageCartAttributes();
    if (!attempt) return Promise.resolve();
    return Promise.race([Promise.resolve(attempt), timeout]);
  }

  function checkoutTriggerFor(event) {
    if (event.type === "submit") {
      return event.submitter && event.submitter.name === "checkout" ? event.target : null;
    }
    if (!event.target || !event.target.closest) return null;
    return event.target.closest('[name="checkout"]');
  }

  function onCheckoutIntent(event) {
    if (checkoutGateBypass || event.defaultPrevented) return;
    var trigger = checkoutTriggerFor(event);
    if (!trigger || !packageAttributesNeedSync()) return;
    var submitter = event.submitter;
    event.preventDefault();
    event.stopImmediatePropagation();
    whenPackageAttributesSynced().then(function () {
      checkoutGateBypass = true;
      try {
        if (event.type === "submit") {
          if (trigger.requestSubmit) trigger.requestSubmit(submitter);
          else trigger.submit();
        } else {
          trigger.click();
        }
      } finally {
        setTimeout(function () { checkoutGateBypass = false; }, 0);
      }
    });
  }

  document.addEventListener("click", onCheckoutIntent, true);
  document.addEventListener("submit", onCheckoutIntent, true);

  function onCartValueClicked(event) {
    var trigger = event.target && event.target.closest && event.target.closest("[data-im-cart-value]");
    if (!trigger || document.getElementById("cart-drawer")) return;
    event.preventDefault();
    window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : "/") + "cart";
  }

  document.addEventListener("click", onCartValueClicked);

  var LINE_REMOVE_ERROR = "We couldn't remove this item. Please try again.";
  var ICON_ERROR = '<svg width="13" viewBox="0 0 13 13" aria-hidden="true"><circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width=".7"></circle><path fill="#fff" d="m5.874 3.528.1 4.044h1.053l.1-4.044zm.627 6.133c.38 0 .68-.288.68-.656s-.3-.656-.68-.656-.681.288-.681.656.3.656.68.656"></path></svg>';

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  function isCartPage() {
    var vars = window.themeVariables;
    if (vars && vars.settings && vars.settings.pageType) return vars.settings.pageType === "cart";
    return window.location.pathname.replace(/\/+$/, "").split("/").pop() === "cart";
  }

  function setLineRemoveError(lineItem, message) {
    var container = lineItem && lineItem.querySelector(".line-item__error-container");
    if (!container) return;
    container.innerHTML = message
      ? '<p class="h-stack gap-1.5 text-sm text-error" role="alert">' + ICON_ERROR + " " + esc(message) + "</p>"
      : "";
  }

  function lineKeyFromRemoveLink(link) {
    try {
      return new URL(link.getAttribute("href"), window.location.origin).searchParams.get("id");
    } catch (e) {
      return null;
    }
  }

  function onLineItemRemoveClicked(event) {
    var link = event.target && event.target.closest && event.target.closest(".line-item__remove-button");
    if (!link) return;
    event.preventDefault();
    event.stopPropagation();
    if (link.getAttribute("aria-busy") === "true") return;

    var lineItem = link.closest("line-item");
    var lineKey = lineKeyFromRemoveLink(link);
    if (!lineKey) { setLineRemoveError(lineItem, LINE_REMOVE_ERROR); return; }

    setLineRemoveError(lineItem, null);
    link.setAttribute("aria-busy", "true");
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    fetch(routeRoot() + "cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    })
      .then(function (response) {
        if (!response.ok) throw new Error("cart/change.js responded " + response.status);
        return response.json();
      })
      .then(function () {
        if (isCartPage()) { window.location.reload(); return; }
        if (lineItem && lineItem.parentNode) lineItem.remove();
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      })
      .catch(function (err) {
        console && console.warn && console.warn("[IronmasterDiscount]", err);
        setLineRemoveError(lineItem, LINE_REMOVE_ERROR);
      })
      .then(function () {
        link.removeAttribute("aria-busy");
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      });
  }

  document.addEventListener("click", onLineItemRemoveClicked, true);

  function repairStaleCartDrawer(event) {
    if (!document.getElementById("cart-drawer")) return;
    var sections = event.detail && event.detail.cart && event.detail.cart.sections;
    if (!sections) return;
    var keys = Object.keys(sections);
    if (!keys.length) return;
    var anyWrapped = keys.some(function (key) {
      var html = sections[key];
      return typeof html === "string" && html.indexOf("shopify-section-" + key) !== -1;
    });
    if (anyWrapped) return;
    document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
  }

  document.addEventListener("cart:change", repairStaleCartDrawer);

  function renderAll() {
    syncPackageCartAttributes();
    var mounts = document.querySelectorAll("[data-im-discount]");
    if (!mounts.length) return;
    var html = renderHTML(lastCartLines, lastCartTotal, null, lastCartDiscounts);
    mounts.forEach(function (el) { el.innerHTML = html; });
  }

  function refresh() {
    return fetchCartLines().then(function (result) {
      lastCartLines = result.lines;
      lastCartTotal = result.cartTotal;
      lastCartAttributes = result.attributes;
      lastCartDiscounts = result.discounts;
      renderAll();
    }).catch(function (err) { /* fail silently — never break the cart */ console && console.warn && console.warn("[IronmasterDiscount]", err); });
  }

  // Re-render mounts whenever Snow re-renders the drawer, then refresh from source.
  document.addEventListener("cart:change", refresh);
  document.addEventListener("cart:refresh", refresh);
  // Snow's <cart-drawer> replaces its ENTIRE innerHTML (wiping our mount) on
  // cart:change, then dispatches "cart-drawer:refreshed" once that swap is
  // done. Re-rendering on cart:change itself loses this race — our content
  // gets inserted, then immediately erased by the drawer's own replaceChildren.
  // "cart-drawer:refreshed" bubbles, so listening on document catches it
  // regardless of which drawer instance fired it.
  document.addEventListener("cart-drawer:refreshed", renderAll);

  if (document.readyState !== "loading") refresh();
  else document.addEventListener("DOMContentLoaded", refresh);

  // Public API — reused by the package builder to preview live discount tiers.
  window.IronmasterDiscount = {
    PACKAGE_RULES: PACKAGE_RULES,
    UI_CONFIG: UI_CONFIG,
    SKU_TAGS: SKU_TAGS,
    tagsForSku: tagsForSku,
    fetchProductTagsForHandles: fetchProductTagsForHandles,
    compareAtPriceForVariant: compareAtPriceForVariant,
    findBestMatch: findBestMatch,
    calculateProgress: calculateProgress,
    calculateSegmentFills: calculateSegmentFills,
    getNextTierNudge: getNextTierNudge,
    renderHTML: renderHTML,
    refresh: refresh
  };
})();
